import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Stack,
  Grid2,
  LinearProgress,
  TextField,
  Chip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SpeedRounded, Search } from '@mui/icons-material';

export default function FacilitySamplingLeaderboard({ reportData = [], stateId }) {
  const reportquarters = [
    { value: 1, label: 'Q1' },
    { value: 2, label: 'Q2' },
    { value: 3, label: 'Q3' },
    { value: 4, label: 'Q4' },
  ];

  // Local state controls for frontend evaluation and filtering metrics
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedType, setSelectedType] = useState('ALL'); // State hook tracking 'ALL', 'QAQC', or 'NORMAL'

  // 1. Initial State Filter Check Block
  const filteredByState = stateId
    ? reportData.filter((r) => String(r.stateId) === String(stateId))
    : reportData;

  // 2. Schedule Validation Profile Matching Check
  const activeSchedules = filteredByState.filter((r) => r.reportId);

  // 3. Frontend Search, Quarter, and Type Selection Multi-tier Resolution Block
  const finalProcessedSchedules = activeSchedules.filter((item) => {
    const matchesSearch = item.facility
      ? item.facility.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    const matchesQuarter = selectedQuarter
      ? Number(item.reportQuarter) === Number(selectedQuarter)
      : true;
    const matchesType =
      selectedType !== 'ALL'
        ? String(item.reportType).toUpperCase() === String(selectedType).toUpperCase()
        : true;
    return matchesSearch && matchesQuarter && matchesType;
  });

  const handleQuarterChipClick = (quarterValue) => {
    if (selectedQuarter === quarterValue) {
      setSelectedQuarter(null); // Deselect if clicked again
    } else {
      setSelectedQuarter(quarterValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
      }}
    >
      {/* HEADER SECTION */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={2}
        mb={2}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <SpeedRounded color="primary" />
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              Data Acquisition Tracker
            </Typography>
          </Stack>
          <Typography variant="caption" color="textSecondary" display="block">
            Real-time tracking of entries logged against total assigned sample points per industry
            asset
          </Typography>
        </Box>

        {/* FRONTEND FILTERS CONTROL BAR */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems="center"
          width={{ xs: '100%', md: 'auto' }}
        >
          {/* SEARCH COMPONENT */}
          <TextField
            size="small"
            placeholder="Search facility..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              sx: { borderRadius: '10px', bgcolor: '#f9fafb', width: { xs: '100%', sm: '180px' } },
            }}
          />

          {/* REPORT TYPE SELECT SELECTION MATRIX */}
          <Select
            value={selectedType}
            size="small"
            onChange={(e) => setSelectedType(e.target.value)}
            sx={{
              borderRadius: '10px',
              minWidth: 120,
              fontWeight: 600,
              bgcolor: '#f9fafb',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <MenuItem value="ALL">All Types</MenuItem>
            <MenuItem value="QAQC">QAQC</MenuItem>
            <MenuItem value="NORMAL">Normal</MenuItem>
          </Select>

          {/* QUARTER CHIPS STRIP */}
          <Stack
            direction="row"
            spacing={0.5}
            width={{ xs: '100%', sm: 'auto' }}
            justifyContent="center"
          >
            {reportquarters.map((q) => {
              const isSelected = selectedQuarter === q.value;
              return (
                <Chip
                  key={q.value}
                  label={q.label}
                  size="small"
                  clickable
                  onClick={() => handleQuarterChipClick(q.value)}
                  onDelete={isSelected ? () => setSelectedQuarter(null) : undefined}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  sx={{
                    fontWeight: 700,
                    borderRadius: '8px',
                    height: '32px',
                    fontSize: '0.75rem',
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      {/* SCROLLABLE INNER GRID BOX LAYOUT CONTAINER */}
      <Box sx={{ maxHeight: '380px', overflowY: 'auto', pr: 0.5, mt: 1 }}>
        <Grid2 container spacing={3}>
          {finalProcessedSchedules.map((item, index) => {
            const progress = Math.round(Number(item.resultProgressPercentage || 0));
            let progressColor = '#ff4842'; // lagging
            if (progress >= 25 && progress < 75) progressColor = '#ed6c02'; // ongoing progress
            if (progress >= 75) progressColor = '#00ab55'; // close to completion / completed

            return (
              <Grid2 size={{ xl: 4, lg: 6, md: 6, sm: 12, xs: 12 }} key={index}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fafbff',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': {
                      boxShadow: '0px 4px 12px rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1} mb={1}>
                    <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: '75%' }}>
                      {item.facility}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ color: progressColor }}>
                      {progress}% Done
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      mb: 1.5,
                      bgcolor: alpha(progressColor, 0.1),
                      '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 3 },
                    }}
                  />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="textSecondary">
                      Q{item.reportQuarter || '1'} • {item.reportType || 'N/A'} • {item.reportYear}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="text.primary">
                      Logs: {item.entriesCount || 0} / {item.totalSamplePoints || 0} Points
                    </Typography>
                  </Stack>
                </Box>
              </Grid2>
            );
          })}

          {finalProcessedSchedules.length === 0 && (
            <Grid2 size={12}>
              <Box sx={{ py: 8, textAlign: 'center', color: 'text.disabled', fontStyle: 'italic' }}>
                No active schedules match your current filter selection profile.
              </Box>
            </Grid2>
          )}
        </Grid2>
      </Box>
    </Paper>
  );
}
